import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Table, Progress, List, Avatar, Tag, Button, Space } from 'antd';
import {
  ShoppingOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons';
import { Line } from '@ant-design/plots';
import useStore from '../store/useStore';
import { useNavigate } from 'react-router-dom';
import AIChatbot from '../components/common/AIChatbot';
import { reportsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';

const Dashboard = () => {
  const navigate = useNavigate();
  const { dashboardData, setDashboardData } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await reportsAPI.getDashboardData();
      const data = response.data;
      
      setDashboardData(data.summary);
      setSalesData(data.salesChart || []);
      setLowStockItems(data.lowStockItems || []);
      setRecentOrders(data.recentOrders || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setDashboardData]);

  const statCards = [
    {
      title: 'Total Products',
      value: dashboardData?.totalProducts || 0,
      icon: <ShoppingOutlined className="text-blue-500" />,
      color: 'blue',
      trend: '+12%',
      trendUp: true,
    },
    {
      title: 'Low Stock Items',
      value: dashboardData?.lowStockItems || 0,
      icon: <ExclamationCircleOutlined className="text-red-500" />,
      color: 'red',
      trend: '-5%',
      trendUp: false,
    },
    {
      title: 'Pending Orders',
      value: dashboardData?.pendingOrders || 0,
      icon: <FileTextOutlined className="text-orange-500" />,
      color: 'orange',
      trend: '+8%',
      trendUp: true,
    },
    {
      title: 'Total Revenue',
      value: dashboardData?.totalRevenue || 0,
      prefix: '$',
      precision: 2,
      icon: <DollarOutlined className="text-green-500" />,
      color: 'green',
      trend: '+15%',
      trendUp: true,
    },
  ];

  const orderColumns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <span className="font-mono text-sm">#{id}</span>,
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          pending: 'orange',
          processing: 'blue',
          shipped: 'green',
          delivered: 'green',
          cancelled: 'red',
        };
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => `$${total?.toFixed(2)}`,
    },
  ];

  const salesChartConfig = {
    data: salesData,
    xField: 'date',
    yField: 'sales',
    smooth: true,
    color: '#1890ff',
    point: {
      size: 3,
      shape: 'circle',
    },
    tooltip: {
      formatter: (datum) => ({
        name: 'Sales',
        value: `$${datum.sales?.toFixed(2)}`,
      }),
    },
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading dashboard..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchDashboardData}
        title="Failed to load dashboard"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with your inventory.</p>
      </div>

      {/* Quick Action Tiles (compact, colorful) */}
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={8}>
          <Card
            hoverable
            onClick={() => navigate('/products')}
            className="cursor-pointer"
            styles={{ body: { padding: 12, color: '#fff' } }}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs opacity-90">Inventory</div>
                <div className="text-lg font-semibold leading-5">Manage Products</div>
              </div>
              <div className="text-2xl"><ShoppingOutlined /></div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            hoverable
            onClick={() => navigate('/orders')}
            className="cursor-pointer"
            styles={{ body: { padding: 12, color: '#fff' } }}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs opacity-90">Sales</div>
                <div className="text-lg font-semibold leading-5">Orders & Quotes</div>
              </div>
              <div className="text-2xl"><DollarOutlined /></div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            hoverable
            onClick={() => navigate('/purchase-orders')}
            className="cursor-pointer"
            styles={{ body: { padding: 12, color: '#fff' } }}
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs opacity-90">Purchasing</div>
                <div className="text-lg font-semibold leading-5">Purchase Orders</div>
              </div>
              <div className="text-2xl"><FileTextOutlined /></div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        {statCards.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card className="h-full">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">{stat.title}</p>
                  <Statistic
                    value={stat.value}
                    prefix={stat.prefix}
                    precision={stat.precision}
                    className="mb-2"
                  />
                  <div className="flex items-center gap-1">
                    {stat.trendUp ? (
                      <RiseOutlined className="text-green-500 text-xs" />
                    ) : (
                      <FallOutlined className="text-red-500 text-xs" />
                    )}
                    <span
                      className={`text-xs ${
                        stat.trendUp ? 'text-green-500' : 'text-red-500'
                      }`}
                    >
                      {stat.trend}
                    </span>
                    <span className="text-gray-400 text-xs">vs last month</span>
                  </div>
                </div>
                <div className="text-3xl">{stat.icon}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Charts and AI Column */}
      <Row gutter={[16, 16]}>
        {/* Sales Chart */}
        <Col xs={24} lg={16}>
          <Space direction="vertical" size={12} className="w-full">
            <Card title="Sales Overview" className="h-full" styles={{ body: { paddingBottom: 8 } }}>
              <div style={{ height: 220 }}>
                <Line {...salesChartConfig} />
              </div>
            </Card>
            <Card title="Recent Orders" size="small" styles={{ body: { padding: 12, maxHeight: 260, overflowY: 'auto' } }}>
              <List
                dataSource={recentOrders.slice(0, 6)}
                renderItem={(o) => (
                  <List.Item>
                    <div className="flex justify-between w-full text-xs">
                      <span className="font-mono">#{o.id}</span>
                      <span className="truncate mx-2">{o.customerName}</span>
                      <span>${o.total?.toFixed(2)}</span>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </Space>
        </Col>

        {/* Right Column: AI Insights + Low Stock */}
        <Col xs={24} lg={8}>
          <Space direction="vertical" size={12} className="w-full">
            <Card title="AI Inventory Summary" size="small" styles={{ body: { padding: 12 } }}>
              <div className="space-y-1.5 text-xs">
                <div>
                  Overall stock levels are {dashboardData?.lowStockItems > 0 ? 'stable with some low-stock alerts' : 'healthy' }.
                </div>
                <div>
                  Low-stock SKUs: <strong>{dashboardData?.lowStockItems || 0}</strong>
                </div>
                <div>
                  Pending orders: <strong>{dashboardData?.pendingOrders || 0}</strong>
                </div>
                <div>
                  Revenue (to-date): <strong>${(dashboardData?.totalRevenue || 0).toLocaleString()}</strong>
                </div>
                <div className="text-gray-500">
                  Tip: Ask the AI chat to list top 5 low-stock items or open Purchasing to create a PO.
                </div>
              </div>
            </Card>

            <Card title="Low Stock Alert" size="small" styles={{ body: { padding: 12, maxHeight: 220, overflowY: 'auto' } }}>
              <List
                dataSource={lowStockItems.slice(0, 5)}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          src={item.image}
                          icon={<ShoppingOutlined />}
                          size="small"
                        />
                      }
                      title={<span className="text-sm">{item.name}</span>}
                      description={
                        <div className="space-y-1">
                          <div className="text-xs text-gray-500">
                            Stock: {item.currentStock} / {item.reorderPoint}
                          </div>
                          <Progress
                            percent={(item.currentStock / item.reorderPoint) * 100}
                            size="small"
                            status={item.currentStock <= item.reorderPoint ? 'exception' : 'normal'}
                            showInfo={false}
                          />
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Space>
        </Col>
      </Row>

      
      <AIChatbot />
    </div>
  );
};

export default Dashboard;

